import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Receipt, 
  ShoppingCart, 
  Calendar, 
  TrendingDown, 
  User,
  AlertCircle,
  Trash2,
  Banknote,
  Landmark
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { saleReturnsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import dayjs from '../../utils/dayjs';

const SaleReturnDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saleReturn, setSaleReturn] = useState(null);

  useEffect(() => {
    loadSaleReturnDetails();
  }, [id]);

  const loadSaleReturnDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load sale return details
      const { data: sr } = await saleReturnsAPI.get(id);
      setSaleReturn(sr);
    } catch (error) {
      console.error('Error loading sale return details:', error);
      setError('Failed to load sale return details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/sale-return/edit/${id}`);
  };

  // const handleDelete = async () => {
  //   if (window.confirm('Are you sure you want to delete this sale return?')) {
  //     try {
  //       await saleReturnsAPI.remove(id);
  //       navigate('/sale-return');
  //     } catch (error) {
  //       console.error('Error deleting sale return:', error);
  //       setError('Failed to delete sale return');
  //     }
  //   }
  // };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/sale-return')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sale Returns
          </button>
        </div>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  if (!saleReturn) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/sale-return')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sale Returns
          </button>
        </div>
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
          Sale return not found
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6 min-h-screen bg-white">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/sale-return')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sale Returns
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Receipt className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Sale Return Details #{saleReturn.ID}
              </h1>
              <p className="text-gray-600">
                View comprehensive details for this sale return
              </p>
            </div>
                    {/* Action Buttons */}
            {/* <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Summary Card */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Return Summary
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Return Date</p>
                  <p className="font-medium text-gray-900">
                    {dayjs(saleReturn.SaleReturnDate).format('DD MMM YYYY, hh:mm A')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {dayjs(saleReturn.SaleReturnDate).format('hh:mm A')}
                  </p>
                </div>
              </div>


                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-medium text-gray-900">{ 'Counter Sale'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Customer Name</p>
                    <p className="font-medium text-gray-900">{(saleReturn.exchange || saleReturn.sale)?.CustomerName || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Customer Phone</p>
                    <p className="font-medium text-gray-900">{(saleReturn.exchange || saleReturn.sale)?.CustomerPhone || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {saleReturn.PaymentMethod === 'bank' ? (
                    <Landmark className="h-5 w-5 text-blue-500" />
                  ) : (
                    <Banknote className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Refunded Via</p>
                    <p className="font-medium text-gray-900">
                      {saleReturn.PaymentMethod === 'bank' ? 'Bank' : 'Cash'}
                    </p>
                  </div>
                </div>


              {saleReturn.woocommerce_id ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm text-gray-500">Web Order#</p>
                    <p className="font-medium text-gray-900">
                      #{saleReturn.woocommerce_id}{saleReturn.brand ? ` (${saleReturn.brand})` : ''}
                      {(saleReturn.wc_sync_status === 'synced' || saleReturn.wc_sync_status === 'failed') ? ` • ${saleReturn.wc_sync_status}` : ''}
                    </p>
                  </div>
                </div>
              ) : saleReturn.Exchange_ID ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-sm text-gray-500">Original Exchange#</p>
                    <a
                      href={`/exchange/${saleReturn.Exchange_ID}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-purple-600 hover:underline"
                    >
                      {saleReturn.exchange?.ExchangeNo || `#${saleReturn.Exchange_ID}`}
                    </a>
                  </div>
                </div>
              ) : saleReturn.Sale_ID ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-sm text-gray-500">Original Sale#</p>
                    <a
                      href={`/sales/${saleReturn.Sale_ID}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-blue-600 hover:underline"
                    >
                      #{saleReturn.sale?.SaleNo || saleReturn.Sale_ID}
                    </a>
                  </div>
                </div>
              ) : null}
            </div>

            {saleReturn.Remarks && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Remarks</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {saleReturn.Remarks}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sale Return Details */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Return Details
            </h3>
            
            {saleReturn.details && saleReturn.details.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {saleReturn.details.map((item, index) => {
                      const subtotal = parseFloat(item.Quantity) * parseFloat(item.Price);
                      const discount = (parseFloat(item.Discount) || 0) * (parseFloat(item.Quantity) || 1);
                      const total = subtotal - discount;
                      
                      return (
                        <tr key={item.ID} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.item?.Name || `Item ${item.SaleItem_ID}`}
                              </p>
                              <p className="text-xs text-gray-500">ID: {item.SaleItem_ID}{item.item?.Barcode ? ` · Barcode: ${item.item.Barcode}` : ''}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {item.Quantity}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-green-600">
                              {formatPrice(item.Price)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-red-600">
                              -{formatPrice(discount)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-bold text-red-600">
                              {formatPrice(total)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Return Totals */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">

                  <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">Discount:</span>
                  <span className="font-semibold text-red-600">
                    -{formatPrice(saleReturn.details.reduce((discount, item) => {
                         return discount += (parseFloat(item.Discount) || 0) * parseFloat(item.Quantity) || 0;
                    }, 0))}
                  </span>
                  </div>

                  <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">Additional Discount:</span>
                  <span className="font-semibold text-red-600">
                    -{formatPrice(saleReturn.Discount)}
                  </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700">Return Total:</span>
                    <span className="font-semibold text-red-600">
                      {formatPrice(saleReturn.details.reduce((total, item) => {
                        const subtotal = parseFloat(item.Quantity) * parseFloat(item.Price);
                        const discount = (parseFloat(item.Discount) || 0) * (parseFloat(item.Quantity) || 0);
                        return total += (subtotal - discount );
                      }, 0) - (parseFloat(saleReturn.Discount) || 0))}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No return details available</p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SaleReturnDetail;
